import * as dagre from "dagre";
import { useFormEditorContext } from "../../../Contexts/FormEditorContext";
import {
    Action,
    ActionType,
    AddBranchAction,
    AddGroupAction,
    AddLogicAction,
    AddRuleAction,
    DuplicateBranchAction,
    DuplicateGroupAction,
    EditBranchAction,
    EditLogicAction,
    EditRuleAction,
    RemoveBranchAction,
    RemoveGroupAction,
    RemoveLogicAction,
    RemoveRuleAction,
    ReorderLogicsAction,
} from "../types/zustand.ts";
import { Branch, Group, Logic } from "../types/logic.ts";
import { Node, Edge, MarkerType } from "@xyflow/react";
import { UNSELECTABLE_FIELD_KINDS } from "../constants/index.ts";
import { Rule } from "../types/rule.ts";
import { useEffect, useState } from "react";
import { id } from "../utils/string.ts";

// Node dimensions and spacing
const nodeWidth = 170;
const nodeHeight = 127;
const horizontalSpacing = 200; // Spacing between nodes

export function useLogicV2() {
    const {
        welcomeScreen,
        fields,
        endScreen,
        logics,
        setLogics,
        selectedField,
    } = useFormEditorContext();

    const logicToNodesAndEdges = (
        fields: any[],
        logics: any[],
        welcomeScreen: any,
        endScreen: any,
    ) => {
        const nodes: Node[] = [];
        const edges: Edge[] = [];

        // Make sure welcome screen exists
        const hasWelcomeScreen = welcomeScreen?.id !== "";
        // Make sure end screen exists
        const hasEndScreen = endScreen?.id !== "";

        // If the welcome screen exists, add it to the nodes
        if (hasWelcomeScreen) {
            nodes.push({
                id: welcomeScreen.id,
                position: {
                    x: 0,
                    y: 0,
                },
                data: {
                    fieldId: welcomeScreen.id,
                    type: "welcome_screen",
                    label: welcomeScreen.title,
                    index: 0,
                    isFirstField: true,
                    isLastField: false,
                },
                selectable:
                    !UNSELECTABLE_FIELD_KINDS.includes("welcome_screen"),
                type: "customNode",
            });
        }

        // Map each field to a node
        fields.forEach((field, index) => {
            nodes.push({
                id: field.id,
                position: {
                    x: 0,
                    y: 0,
                },
                selected: field.id === selectedField?.id,
                data: {
                    fieldId: field.id,
                    type: field.type,
                    label: field.title,
                    index: hasWelcomeScreen ? index + 1 : index,
                    isFirstField: hasWelcomeScreen ? false : index === 0,
                    isLastField: hasEndScreen
                        ? false
                        : index === fields.length - 1,
                },
                selectable: !UNSELECTABLE_FIELD_KINDS.includes(field.type),
                type: "customNode",
            });
        });

        // If the end screen exists, add it to the nodes
        if (hasEndScreen) {
            nodes.push({
                id: endScreen.id,
                position: {
                    x: 0,
                    y: 0,
                },
                data: {
                    fieldId: endScreen.id,
                    type: "end_screen",
                    label: endScreen.title,
                    index: fields.length - 1,
                    isFirstField: false,
                    isLastField: true,
                },
                selectable: !UNSELECTABLE_FIELD_KINDS.includes("end_screen"),
                type: "customNode",
            });
        }

        // Filter the logics to only include the ones that have a default target field
        const logicsWithDefaultTarget = (logics as Logic[]).filter(
            (logic) => logic.defaultTargetId !== "",
        );

        // Create the edges for each logic
        logicsWithDefaultTarget.forEach((logic) => {
            const startId = logic.fieldId;
            const defaultEndId = logic.defaultTargetId;

            // Create the edge for the default path
            edges.push({
                id: `${startId}->${defaultEndId}`,
                source: startId,
                target: defaultEndId,
                animated: true,
                style: {
                    strokeWidth: 1,
                    stroke: "#44546F",
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 10,
                    height: 10,
                    color: "#44546F",
                },
            });

            // Filter the branches to only include the ones that have a target field
            const branchesWithTarget = logic.branches?.filter(
                (branch) => branch.targetId !== "",
            );

            // Create the edges for each branch
            branchesWithTarget?.forEach((branch) => {
                const startId = logic.fieldId;
                const endId = branch.targetId;

                // Create the edge for the branch
                edges.push({
                    id: `${startId}->${endId}`,
                    source: startId,
                    target: endId,
                    animated: true,
                    style: {
                        strokeWidth: 1,
                        stroke: "#44546F",
                    },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 10,
                        height: 10,
                        color: "#44546F",
                    },
                });
            });
        });

        const { nodes: layoutedNodes, edges: layoutedEdges } =
            getLayoutedElements(nodes, edges);

        return { nodes: layoutedNodes, edges: layoutedEdges };
    };
    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);

    useEffect(() => {
        const { nodes: layoutedNodes, edges: layoutedEdges } =
            logicToNodesAndEdges(fields, logics, welcomeScreen, endScreen);

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
    }, [fields, logics, welcomeScreen, endScreen]);

    const getLayoutedElements = (
        nodes: Node[],
        edges: Edge[],
        direction = "LR",
    ) => {
        // Create a new graph instance for each layout operation
        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setDefaultEdgeLabel(() => ({}));

        dagreGraph.setGraph({
            rankdir: direction,
            nodesep: horizontalSpacing,
            ranksep: horizontalSpacing,
        });

        nodes.forEach((node) => {
            dagreGraph.setNode(node.id, {
                width: nodeWidth,
                height: nodeHeight,
            });
        });

        edges.forEach((edge) => {
            dagreGraph.setEdge(edge.source, edge.target);
        });

        dagre.layout(dagreGraph);

        nodes.forEach((node) => {
            const position = dagreGraph.node(node.id);

            // We are shifting the dagre node position (anchor=center center) to the top left
            // so it matches the React Flow node anchor point (top left).
            node.position = {
                x: position.x - nodeWidth / 2,
                y: position.y - nodeHeight / 2,
            };

            return node;
        });

        return { nodes, edges };
    };

    const dispatch = (action: Action) => {
        switch (action.type) {
            case ActionType.ADD_LOGIC:
                addLogic(action);
                break;

            case ActionType.EDIT_LOGIC:
                editLogic(action);
                break;

            case ActionType.REMOVE_LOGIC:
                removeLogic(action);
                break;

            case ActionType.RESET_LOGIC:
                resetLogic();
                break;

            case ActionType.REORDER_LOGICS:
                reorderLogic(action);
                break;

            case ActionType.ADD_BRANCH:
                addBranch(action);
                break;

            case ActionType.EDIT_BRANCH:
                editBranch(action);
                break;

            case ActionType.DUPLICATE_BRANCH:
                duplicateBranch(action);
                break;

            case ActionType.REMOVE_BRANCH:
                removeBranch(action);
                break;

            case ActionType.ADD_GROUP:
                addGroup(action);
                break;

            case ActionType.DUPLICATE_GROUP:
                duplicateGroup(action);
                break;

            case ActionType.REMOVE_GROUP:
                removeGroup(action);
                break;

            case ActionType.ADD_RULE:
                addRule(action);
                break;

            case ActionType.EDIT_RULE:
                editRule(action);
                break;

            case ActionType.REMOVE_RULE:
                removeRule(action);
                break;

            default:
                break;
        }
    };

    const addLogic = (action: Pick<AddLogicAction, "payload">) => {
        const { fieldId, newFields } = action.payload;

        const newFieldIndex = newFields.findIndex(
            (field) => field.id === fieldId,
        );

        if (newFieldIndex === -1) return;

        // Previous field manipulation
        const previousFieldIndex = newFieldIndex - 1;
        const hasPreviousField = previousFieldIndex >= 0;
        const hasWelcomeScreen = !!welcomeScreen?.id;
        // In case of there's no field presented before the new field, we'll use the welcome screen as the previous field or else null
        const previousField = hasPreviousField
            ? newFields[previousFieldIndex]
            : hasWelcomeScreen
            ? welcomeScreen
            : null;
        const previousFieldLogic = logics.find(
            (logic) => logic.fieldId === previousField?.id,
        );
        // Set the default target id of the previous field to the new field
        if (previousFieldLogic) {
            previousFieldLogic.defaultTargetId = fieldId;
        }

        // Next field manipulation
        const nextFieldIndex = newFieldIndex + 1;
        const hasNextField = nextFieldIndex < newFields.length;
        const hasEndScreen = !!endScreen?.id;
        // In case of there's no field presented after the new field, we'll use the end screen as the next field or else null
        const nextField = hasNextField
            ? newFields[nextFieldIndex]
            : hasEndScreen
            ? endScreen
            : null;
        const nextFieldLogic = logics.find(
            (logic) => logic.fieldId === nextField?.id,
        );

        // Set the default target idz
        const logic: Logic = {
            fieldId,
            defaultTargetId: "",
            branches: [],
        };

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const editLogic = (action: Pick<EditLogicAction, "payload">) => {
        const { fieldId, defaultTargetId } = action.payload;

        // Find the logic of the given fieldId
        const logic = logics.find((logic) => logic.fieldId === fieldId);
        if (!logic) return;

        // Edit the logic
        logic.defaultTargetId = defaultTargetId;

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const removeLogic = (action: Pick<RemoveLogicAction, "payload">) => {
        const { fieldId } = action.payload;

        // Find the logic of the field to be removed
        const logic: Logic = logics.find((logic) => logic.fieldId === fieldId);
        if (!logic) return;

        const nextFieldId = logic.defaultTargetId;

        // Point all the branch targets and default targets that are currently pointed towards the field to be removed to the next field
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.defaultTargetId === fieldId) {
                _logic.defaultTargetId = nextFieldId;
            }
            _logic.branches.forEach((branch) => {
                if (branch.targetId === fieldId) {
                    branch.targetId = nextFieldId;
                }
            });
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const resetLogic = () => {
        // Reset the logics so that every node is connected to the next node sequentially.
        // If there are branches inside a node, all of them will be removed as well.
        // All the nodes are connected using defaultTargetId only.

        // This variable is used to make sure that we are iterating through the fields array sequentially.
        let lastIteratedFieldIndex = 0;
        const modifiedLogics = (logics as Logic[]).map((logic, index) => {
            // If the logic is the welcome screen, we will return the welcome screen logic which we've already handled above.
            if (logic.fieldId === welcomeScreen?.id) {
                // As the welcome screen is placed outside of the fields array, we need to handle it separately.
                const welcomeScreenLogic = (logics as Logic[]).find(
                    (logic) => logic.fieldId === welcomeScreen?.id,
                );
                if (welcomeScreenLogic) {
                    welcomeScreenLogic.defaultTargetId = fields[0]?.id;
                    return welcomeScreenLogic;
                }
            }

            const currentFieldIndex = fields.findIndex(
                (field) => field.id === logic.fieldId,
            );
            const nextFieldIndex = currentFieldIndex + 1;
            const hasNextField = nextFieldIndex < fields.length;
            const hasEndScreen = !!endScreen?.id;
            // If there is no next field, use the end screen as the next field or else null
            const nextFieldId = hasNextField
                ? fields[nextFieldIndex]?.id
                : hasEndScreen
                ? endScreen?.id
                : "";

            lastIteratedFieldIndex++;

            console.log("----------------------", {
                lastIteratedFieldIndex,
                currentField: fields[currentFieldIndex],
                nextField: fields[nextFieldIndex],
                logic,
            });

            return {
                ...logic,
                branches: [],
                defaultTargetId: nextFieldId,
            };
        });
        setLogics(modifiedLogics);
    };

    const reorderLogic = (action: Pick<ReorderLogicsAction, "payload">) => {
        const { newFields } = action.payload;

        console.log("newFields", newFields);

        // In the reorder process, only the default target id is changed.
        // So we need to update the default target id of the logics based on the new fields array.

        // Point the welcome screen to the first field if welcome screen is present
        const welcomeScreenLogic = (logics as Logic[]).find(
            (logic) => logic.fieldId === welcomeScreen?.id,
        );
        if (welcomeScreenLogic) {
            welcomeScreenLogic.defaultTargetId = newFields[0]?.id;
        }

        const hasEndScreen = !!endScreen?.id;

        const modifiedLogics = (logics as Logic[]).map((logic) => {
            // No need to modify the welcome screen logic as we've already handled it above.
            if (logic.fieldId === welcomeScreen?.id) {
                return welcomeScreenLogic;
            }

            const currFieldIndex = newFields.findIndex(
                (field) => field.id === logic.fieldId,
            );
            const nextFieldIndex = currFieldIndex + 1;
            const hasNextField = nextFieldIndex < newFields.length;
            const nextFieldId = hasNextField
                ? newFields[nextFieldIndex]?.id
                : hasEndScreen
                ? endScreen?.id
                : "";

            return {
                ...logic,
                defaultTargetId: nextFieldId,
            };
        });

        setLogics(modifiedLogics);
    };

    const addBranch = (action: Pick<AddBranchAction, "payload">) => {
        const { fieldId } = action.payload;

        // Find the logic of the given fieldId
        const logic = logics.find((logic) => logic.fieldId === fieldId);
        if (!logic) return;

        const branch: Branch = {
            id: id(),
            name: `Branch ${logic.branches.length + 1}`,
            groups: [],
            targetId: "",
        };

        logic.branches.push(branch);

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const editBranch = (action: Pick<EditBranchAction, "payload">) => {
        const { branchId, targetId } = action.payload;

        // Find the logic of the given branchId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) => branch.id === branchId),
        );
        if (!logic) return;

        // Find the branch of the given branchId
        const branch = logic.branches.find((branch) => branch.id === branchId);
        if (!branch) return;

        // Edit the branch
        branch.targetId = targetId;

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const duplicateBranch = (
        action: Pick<DuplicateBranchAction, "payload">,
    ) => {
        const { branchId } = action.payload;

        // Find the logic of the given branchId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) => branch.id === branchId),
        );
        if (!logic) return;

        const branch = logic.branches.find((branch) => branch.id === branchId);
        if (!branch) return;

        // Duplicate the branch
        const newBranch = {
            ...branch,
            id: id(),
        };

        logic.branches.push(newBranch);

        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const removeBranch = (action: Pick<RemoveBranchAction, "payload">) => {
        const { branchId } = action.payload;

        // Find the logic of the given branchId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) => branch.id === branchId),
        );
        if (!logic) return;

        // Remove the branch from the logic
        logic.branches = logic.branches.filter(
            (branch) => branch.id !== branchId,
        );

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const addGroup = (action: Pick<AddGroupAction, "payload">) => {
        const { branchId } = action.payload;

        // Find the logic of the given branchId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) => branch.id === branchId),
        );
        if (!logic) return;

        const group: Group = {
            id: id(),
            name: `Group ${
                logic.branches.find((branch) => branch.id === branchId)?.groups
                    .length + 1
            }`,
            rules: [],
        };

        // Add the group to the branch
        logic.branches
            .find((branch) => branch.id === branchId)
            ?.groups.push(group);

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const duplicateGroup = (action: Pick<DuplicateGroupAction, "payload">) => {
        const { groupId } = action.payload;

        // Find the logic of the given groupId
        const logic: Logic = logics.find((logic) =>
            logic.branches.some((branch) =>
                branch.groups.some((group) => group.id === groupId),
            ),
        );
        if (!logic) return;

        const group = logic.branches
            .find((branch) =>
                branch.groups.some((group) => group.id === groupId),
            )
            ?.groups.find((group) => group.id === groupId);

        if (!group) return;

        // Duplicate the group
        const newGroup = {
            ...group,
            id: id(),
        };

        logic.branches
            .find((branch) =>
                branch.groups.some((group) => group.id === groupId),
            )
            ?.groups.push(newGroup);

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const removeGroup = (action: Pick<RemoveGroupAction, "payload">) => {
        const { groupId } = action.payload;

        // Find the logic of the given groupId
        const logic: Logic = logics.find((logic) =>
            logic.branches.some((branch) =>
                branch.groups.some((group) => group.id === groupId),
            ),
        );
        if (!logic) return;

        // Remove the group from the branch
        logic.branches.forEach((branch) => {
            branch.groups = branch.groups.filter(
                (group) => group.id !== groupId,
            );
        });

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const addRule = (action: Pick<AddRuleAction, "payload">) => {
        const { groupId, sourceId, operator, expectedValue } = action.payload;

        // Find the logic of the given groupId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) =>
                branch.groups.some((group) => group.id === groupId),
            ),
        );
        if (!logic) return;

        // Find the group of the given groupId
        const group = logic.branches
            .find((branch) =>
                branch.groups.some((group) => group.id === groupId),
            )
            ?.groups.find((group) => group.id === groupId);

        if (!group) return;

        const rule: Rule = {
            id: id(),
            sourceId,
            operator,
            expectedValue,
        };

        group.rules.push(rule);

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const editRule = (action: Pick<EditRuleAction, "payload">) => {
        const { ruleId, sourceId, operator, expectedValue } = action.payload;

        // Find the logic of the given ruleId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) =>
                branch.groups.some((group) =>
                    group.rules.some((rule) => rule.id === ruleId),
                ),
            ),
        );
        if (!logic) return;

        // Find the group of the given ruleId
        const group = logic.branches
            .find((branch) =>
                branch.groups.some((group) =>
                    group.rules.some((rule) => rule.id === ruleId),
                ),
            )
            ?.groups.find((group) =>
                group.rules.some((rule) => rule.id === ruleId),
            );
        if (!group) return;

        const newRule = {
            ...group.rules.find((rule) => rule.id === ruleId),
            sourceId,
            operator,
            expectedValue,
        };

        // Edit the rule
        group.rules = group.rules.map((rule) =>
            rule.id === ruleId
                ? { ...rule, sourceId, operator, expectedValue }
                : rule,
        );

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    const removeRule = (action: Pick<RemoveRuleAction, "payload">) => {
        const { ruleId } = action.payload;

        // Find the logic of the given ruleId
        const logic = logics.find((logic) =>
            logic.branches.some((branch) =>
                branch.groups.some((group) =>
                    group.rules.some((rule) => rule.id === ruleId),
                ),
            ),
        );
        if (!logic) return;

        // Find the group of the given ruleId
        const group = logic.branches
            .find((branch) =>
                branch.groups.some((group) =>
                    group.rules.some((rule) => rule.id === ruleId),
                ),
            )
            ?.groups.find((group) =>
                group.rules.some((rule) => rule.id === ruleId),
            );
        if (!group) return;

        // Remove the rule from the group
        group.rules = group.rules.filter((rule) => rule.id !== ruleId);

        // Update the logics
        const modifiedLogics = (logics as Logic[]).map((_logic) => {
            if (_logic.fieldId === logic.fieldId) {
                return logic;
            }
            return _logic;
        });

        setLogics(modifiedLogics);
    };

    return {
        nodes,
        setNodes,
        edges,
        setEdges,
        dispatch,
    };
}
